// The LIB locations console route subtree (console slice — location hierarchy).
// Code-based routes per DEV-GUIDE-002 §4 — a package-composed subtree hand-writes
// `.lazy()` + `createLazyRoute`. This factory hangs the `/library/locations` tree
// off the portal's root route so router.tsx stays the single wiring point
// (composed ALONGSIDE the catalogue/patrons/settings subtrees — none touches
// another).
//
// GATING is per-screen (router.tsx's documented choice): the locations LAYOUT
// route re-checks lib:admin via useSession/<Show> before it renders any child — a
// defence-in-depth mirror of the API's rbac('lib:admin') guard on every location
// WRITE route (the config/write tier is lib:admin — librarian/owner/admin hold
// lib:* → lib:admin). The UI can only HIDE; the gateway is the authority (ADR-001
// §14.2). Anonymous → /login; signed-in-without-the-grant → an explicit denied
// panel, never a blank Outlet. Because the whole subtree already requires
// lib:admin, the screens' write buttons are gated a second time on lib:admin
// (defence-in-depth, mirroring PatronGroups) so relaxing this layout later never
// silently exposes a write.
//
// URL-AS-STATE (DEV-GUIDE-002 §4.1): the hierarchy drills through the SINGLE
// parent filter each level's list query accepts — branches?libraryId, then
// zones?branchId, stacks?zoneId, shelves?stackId. The ancestor ids ABOVE the
// immediate filter ride along so each screen can render a full breadcrumb
// (Library › Branch › Zone › Stack) and scope its parent picker without a detail
// round-trip. zod is not a direct portal dep (the AllBooks note), so these are
// hand-written self-healing validators with the same contract — a garbage param
// heals to `undefined` (show the unfiltered level) rather than throwing.
//
// NO LOADERS (deliberate): every level is an UNPAGED bare-array registry
// (libraries()/branches(id)/zones(id)/…), not a keyset list — there is no
// cursor/limit to warm and no `.catch()` keyset contract to honour, so the
// screens fetch on mount via useQuery and the routes stay loader-free. Add a
// loader→`void prefetchQuery(libraryQueries.<list>)` only if a level ever wants
// server-warmed first paint.
import { lazyRoute } from "@erp/ui/route-kit";
import { Outlet, createRoute } from "@tanstack/solid-router";
import { ConsoleLayout } from "@erp/ui/console-layout";
import { HubGrid } from "@erp/ui/hub-grid";
import { PageHeading } from "@erp/ui/page-heading";
import { MODULE_HUE } from "@erp/ui/category-hue";
import { MapPin } from "lucide-solid";
import { panelSearch, type PanelSearch } from "@erp/ui/side-panel";
import type { AnyRoute } from "@tanstack/solid-router";

/* ───────────────────────────── URL-as-state (drill chain) ─────────────────────
 * Each child level filters by its immediate parent; the higher ancestor ids come
 * along for the breadcrumb + a scoped parent picker. All optional — a bare tab
 * click (no search) shows the whole level. */
function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/* ───────────────────────────── URL-as-state (side panels) ────────────────────
 * Every level's add/edit form is a `@erp/ui/side-panel` SidePanel, and a panel is
 * a VIEW — so its open/target/expanded state rides the SAME search params the
 * drill chain does (`?panel=new|edit`, `?panelId=<recordId>`, `?panelWide=1`).
 * `panelSearch` heals them against this CLOSED set, so a stale or hand-edited
 * link can never ask a level to open something it cannot render. The panel
 * params are ADDITIVE — the drill chain above is untouched, and the two compose
 * (`?libraryId=…&panel=edit&panelId=…` is a deep link into one branch's form). */
export const LOCATION_PANELS = ["new", "edit"] as const;
export type LocationPanel = (typeof LOCATION_PANELS)[number];

export type LibrariesSearch = PanelSearch;

export interface BranchesSearch extends PanelSearch {
  libraryId?: string;
}
export interface ZonesSearch extends PanelSearch {
  libraryId?: string;
  branchId?: string;
}
export interface StacksSearch extends PanelSearch {
  libraryId?: string;
  branchId?: string;
  zoneId?: string;
}
export interface ShelvesSearch extends PanelSearch {
  libraryId?: string;
  branchId?: string;
  zoneId?: string;
  stackId?: string;
}

export function validateLibrariesSearch(input: Record<string, unknown>): LibrariesSearch {
  return { ...panelSearch(LOCATION_PANELS, input) };
}
export function validateBranchesSearch(input: Record<string, unknown>): BranchesSearch {
  return { libraryId: optionalString(input.libraryId), ...panelSearch(LOCATION_PANELS, input) };
}
export function validateZonesSearch(input: Record<string, unknown>): ZonesSearch {
  return {
    libraryId: optionalString(input.libraryId),
    branchId: optionalString(input.branchId),
    ...panelSearch(LOCATION_PANELS, input),
  };
}
export function validateStacksSearch(input: Record<string, unknown>): StacksSearch {
  return {
    libraryId: optionalString(input.libraryId),
    branchId: optionalString(input.branchId),
    zoneId: optionalString(input.zoneId),
    ...panelSearch(LOCATION_PANELS, input),
  };
}
export function validateShelvesSearch(input: Record<string, unknown>): ShelvesSearch {
  return {
    libraryId: optionalString(input.libraryId),
    branchId: optionalString(input.branchId),
    zoneId: optionalString(input.zoneId),
    stackId: optionalString(input.stackId),
    ...panelSearch(LOCATION_PANELS, input),
  };
}

const LOCATIONS_LINKS = [
  { to: "/library/locations/libraries", label: "Libraries", blurb: "Libraries and the location tree", id: "locations-nav-libraries" },
  { to: "/library/locations/branches", label: "Branches", blurb: "Branch registry per library", id: "locations-nav-branches" },
  { to: "/library/locations/zones", label: "Zones", blurb: "Zones within a branch", id: "locations-nav-zones" },
  { to: "/library/locations/stacks", label: "Stacks", blurb: "Stacks (ranges) within a zone", id: "locations-nav-stacks" },
  { to: "/library/locations/shelves", label: "Shelves", blurb: "Shelves within a stack", id: "locations-nav-shelves" },
  { to: "/library/locations/map", label: "Map Builder", blurb: "Floor-plan grid designer", id: "locations-nav-map" },
] as const;

/**
 * The locations shell: gates on lib:admin, then renders the active child.
 * Gating sequence lives in @erp/ui/console-layout; the tab strip is retired in
 * favour of the shell sidebar (module manifest) + header breadcrumbs.
 */
function LocationsLayout() {
  return (
    <ConsoleLayout
      perm="lib:admin"
      deniedMessage="Only a library administrator can manage locations."
      skeletonId="locations-skeleton"
      deniedId="locations-denied"
      unreachableId="locations-unreachable"
    >
      <Outlet />
    </ConsoleLayout>
  );
}

/** `/library/locations` index — the landing with cards into each level. */
function LocationsLanding() {
  return (
    <div>
      {/* The console's identity capsule (DEV-GUIDE-002 §12.5). Locations wears
          the OPERATIONS chartreuse (lane L2's call, mirrored on the Overview
          card): the tree of buildings and shelves is how the library RUNS, and
          every locations screen carries that capsule, so the landing does too. */}
      <PageHeading
        icon={MapPin}
        hue={MODULE_HUE.operations}
        title="Locations"
        subtitle="Libraries, their branches, and the zones, stacks and shelves inside them"
        help={{
          title: "The building, top down",
          children: (
            <p>
              A library has branches, a branch has zones, a zone has stacks and a stack has shelves. Set each
              level here so a copy can be placed on an exact shelf and found there again; the Map Builder draws
              the floor plan those placements follow. Each card opens one level of the tree.
            </p>
          ),
        }}
      />
      {/* Was a hand-drawn grid of Link > Card > CardHeader with a green
          `StatusBadge label="Open"` for a button — see @erp/ui/hub-grid for
          why both were wrong, and for the one place that shape now lives. */}
      <HubGrid id="locations-landing" items={LOCATIONS_LINKS} hue={MODULE_HUE.operations} columns={3} />
    </div>
  );
}

/**
 * Build the `/library/locations` subtree under a portal's root route. Returns the
 * routes to spread into `rootRoute.addChildren([...])` (router.tsx). Screens close
 * their own `.lazy()` loop with `createLazyRoute('/library/locations/<x>')`.
 *
 * The four child levels carry a self-healing `validateSearch` for the drill chain
 * (libraryId → branchId → zoneId → stackId); the screen reads it with
 * `Route.useSearch()` and drills with `Route.useNavigate({ to, search })`.
 */
export function makeLibLocationsRoutes<TParent extends AnyRoute>(rootRoute: TParent) {
  const locationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "library/locations",
    component: LocationsLayout,
  });

  const indexRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "/",
    component: LocationsLanding,
  });

  const librariesRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "libraries",
    validateSearch: validateLibrariesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/locations/MyLibraries.js")));

  const branchesRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "branches",
    validateSearch: validateBranchesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/locations/Branches.js")));

  const zonesRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "zones",
    validateSearch: validateZonesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/locations/Zones.js")));

  const stacksRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "stacks",
    validateSearch: validateStacksSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/locations/Stacks.js")));

  const shelvesRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "shelves",
    validateSearch: validateShelvesSearch,
  }).lazy(lazyRoute(() => import("../../screens/admin/locations/Shelves.js")));

  // Map Builder — DELIBERATELY LEFT A STUB (task scope). The floor-plan grid rides
  // UpdateStack (gridX/Y/width/height), but the geometry-authoring API is partial;
  // building the designer is out of this slice. The route stays wired so the tab
  // renders its "not built yet" notice rather than 404ing.
  const mapRoute = createRoute({
    getParentRoute: () => locationsRoute,
    path: "map",
  }).lazy(lazyRoute(() => import("../../screens/admin/locations/MapBuilder.js")));

  return [
    locationsRoute.addChildren([
      indexRoute,
      librariesRoute,
      branchesRoute,
      zonesRoute,
      stacksRoute,
      shelvesRoute,
      mapRoute,
    ]),
  ];
}
